import React, { MutableRefObject, ReactNode, createContext, useContext, useRef, useState } from 'react'

import { ChatMessage } from '../../types/chat'

interface ChatContextType {
    messages: ChatMessage[]
    addMessage: (message: ChatMessage) => void
    isOpen: boolean
    toggleOpen: () => void
    setOpen: (open: boolean) => void
    unreadCount: number
    setUnreadCount: (count: number) => void
    resetSession: () => void
    sessionEpochRef: MutableRefObject<number>
    chatSessionId: string | null
    setChatSessionId: (id: string | null) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

const WELCOME_MESSAGE: ChatMessage = {
    id: 0,
    role: 'assistant',
    content:
        "Hi! I'm your experimentation assistant. Ask me anything about A/B testing, statistical concepts, metrics, or how the platform works.",
}

export const ChatProvider = ({ children }: { children: ReactNode }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE])
    const [isOpen, setIsOpen] = useState(false)
    const [unreadCount, setUnreadCount] = useState(0)
    const [chatSessionId, setChatSessionId] = useState<string | null>(null)
    const sessionEpochRef = useRef(0)

    const addMessage = (message: ChatMessage) => setMessages((prev) => [...prev, message])

    const toggleOpen = () => {
        setIsOpen((prev) => {
            if (!prev) setUnreadCount(0)
            return !prev
        })
    }

    const setOpen = (open: boolean) => {
        if (open) setUnreadCount(0)
        setIsOpen(open)
    }

    const resetSession = () => {
        sessionEpochRef.current += 1
        setMessages([WELCOME_MESSAGE])
        setChatSessionId(null)
        setUnreadCount(0)
    }

    return (
        <ChatContext.Provider
            value={{
                messages,
                addMessage,
                isOpen,
                toggleOpen,
                setOpen,
                unreadCount,
                setUnreadCount,
                resetSession,
                sessionEpochRef,
                chatSessionId,
                setChatSessionId,
            }}
        >
            {children}
        </ChatContext.Provider>
    )
}

export const useChatContext = (): ChatContextType => {
    const context = useContext(ChatContext)
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider')
    }
    return context
}
